// Lookup helpers over an in-memory InsurerData tree. The tree comes from
// getInsurers() in insurer-cms.ts (server). Pure functions — no module state.
import type {
  InsurerData,
  Insurer,
  Coverage,
  ItemizedCoverage,
  InsurerProformaConfig,
  Package,
} from "./insurance-types"
import { embedCategorySums } from "../proforma/catalog"

export {
  getServices,
  findService,
  getServiceName,
  effectiveServicePrice,
  services,
} from "./insurance-services"

export function findInsurer(
  data: InsurerData,
  id: string
): Insurer | undefined {
  return data.insurers.find((ins) => ins.id === id)
}

export function findPackage(
  insurer: Insurer,
  packageId: string
): Package | undefined {
  return insurer.packages.find((p) => p.id === packageId)
}

export function getCoverage(
  data: InsurerData,
  insurerId: string,
  packageId: string,
  serviceId: string
): Coverage | null {
  const insurer = findInsurer(data, insurerId)
  if (!insurer) return null
  const pkg = findPackage(insurer, packageId)
  if (!pkg) return null
  // Membership tiers are bundled blood tests — reuse the package's blood-test
  // coverage. No membership coverage rows exist in the CMS by design.
  let cov = pkg.coverage[serviceId]
  if (!cov && serviceId.startsWith("membership-")) {
    cov = pkg.coverage["blood-test"]
  }
  if (!cov || !cov.type) return null
  return cov
}

export function formatCHF(amount: number): string {
  return String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, "’")
}

// Which result panel a coverage row lands in. `itemized` (Groupe Mutuel) is
// deliberately an ESTIMATE, not confirmed: the amount is concrete (a % of the
// covered TARDOC categories, not of the list price) but the insurer hasn't
// committed to paying it, so it renders in the blue "likely" panel with no
// reimbursement guarantee. Only fixed/percentage rows are insurer-confirmed.
export function isConfirmedCoverage(coverage: Coverage | null): boolean {
  return coverage?.type === "fixed" || coverage?.type === "percentage"
}

export function isEstimatedCoverage(coverage: Coverage | null): boolean {
  return coverage?.type === "likely" || coverage?.type === "itemized"
}

/**
 * The figures that identify WHICH allowance a coverage row draws on. `null`
 * for a not_covered row, which grants nothing to share.
 */
function envelopeFigures(coverage: Coverage): {
  percentage: number | null
  amount: number | null
  ceiling: number | null
  period: string | null
} | null {
  if (coverage.type === "not_covered") return null
  if (coverage.type === "fixed") {
    return {
      percentage: null,
      amount: coverage.amount,
      ceiling: coverage.amount,
      period: coverage.period ?? null,
    }
  }
  return {
    percentage: coverage.percentage ?? null,
    amount: null,
    ceiling: coverage.max ?? null,
    period: coverage.period ?? null,
  }
}

/**
 * The most a coverage row can pay inside its own period, or null when it is
 * uncapped. A `fixed` row IS its own ceiling; the others carry it as `max`.
 */
export function coverageCeiling(coverage: Coverage): number | null {
  return envelopeFigures(coverage)?.ceiling ?? null
}

/**
 * Whether two coverage rows are ONE insurer allowance rather than two.
 *
 * An insurer writes a prevention benefit as a single allowance, and the CMS
 * stores that one row against every service that can be claimed against it.
 * Atupri is "90% of costs up to max. CHF 200 per year", Visana "90% for a
 * check-up every 3 years, max. CHF 600", CSS "50%, max. CHF 500 every 2
 * calendar years" — one pot each, not one per service. So two priced lines
 * that resolve to rows with the same figures have to be capped together.
 *
 * Where an insurer genuinely grants separate allowances the figures differ and
 * this returns false, so those keep adding up: KPT pays "max. CHF 1'500 per
 * preventive examination" for the scan plus a separate "75%, max. CHF 4'000"
 * for the blood test, and AXA CHF 300 per calendar year for the scan against
 * CHF 500 per three years for the blood test.
 *
 * `type` is deliberately not compared: Groupe Mutuel stores the scan as
 * `itemized` and the blood test as `likely` with identical figures, and that
 * is still one allowance.
 */
export function sharesEnvelope(a: Coverage, b: Coverage): boolean {
  const x = envelopeFigures(a)
  const y = envelopeFigures(b)
  if (!x || !y) return false
  return (
    x.percentage === y.percentage &&
    x.amount === y.amount &&
    x.ceiling === y.ceiling &&
    x.period === y.period
  )
}

export function getCoverageAmount(coverage: Coverage): number | null {
  if (coverage.type === "fixed") return coverage.amount
  if (coverage.type === "percentage") return coverage.max ?? null
  return null
}

export function calculateReimbursement(
  coverage: Coverage,
  servicePrice: number
): number | null {
  if (coverage.type === "fixed") {
    return Math.min(servicePrice, coverage.amount)
  }
  // `!= null` (loose) catches both null and undefined — belt-and-braces with the
  // CMS mapper coercion: a stray null max would make Math.min(raw, null) === 0
  // ("CHF 0 covered" — the GM Global Classic regression, AHD-7034).
  if (coverage.type === "percentage") {
    const raw = Math.round((servicePrice * coverage.percentage) / 100)
    return coverage.max != null ? Math.min(raw, coverage.max) : raw
  }
  if (coverage.type === "likely" && coverage.percentage != null) {
    const raw = Math.round((servicePrice * coverage.percentage) / 100)
    return coverage.max != null ? Math.min(raw, coverage.max) : raw
  }
  return null
}

// Itemized reimbursement for partial-coverage insurers. The base is the sum of
// the covered TARDOC categories' line items — read straight from the local
// catalog (lib/proforma), so there's no snapshot to sync and no API to call.
// `serviceId` is an embed-service id (e.g. "pro-mri", "membership-plus-3").
// Add-on accumulation is layered in when the form threads selected add-ons.
export function calculateItemizedReimbursement(
  coverage: ItemizedCoverage,
  serviceId: string
): number {
  const sums = embedCategorySums(serviceId)
  const base = coverage.coveredCategories.reduce(
    (acc, cat) => acc + sums[cat],
    0
  )
  const raw = Math.round((base * coverage.percentage) / 100)
  // Same null-max guard as above (AHD-7034): cap only when max is set.
  return coverage.max != null ? Math.min(raw, coverage.max) : raw
}

// Single dispatch point for "how much does this coverage reimburse": itemized
// reads the catalog by serviceId, every other type scales the list price. Keeps
// the itemized-vs-standard branch in one place so call sites can't drift.
// Returns null for not_covered (calculateReimbursement does); callers default.
export function calculateCoverageReimbursement(
  coverage: Coverage,
  serviceId: string,
  servicePrice: number
): number | null {
  return coverage.type === "itemized"
    ? calculateItemizedReimbursement(coverage, serviceId)
    : calculateReimbursement(coverage, servicePrice)
}

export function getProformaConfig(
  data: InsurerData,
  insurerId: string
): InsurerProformaConfig | null {
  const insurer = findInsurer(data, insurerId)
  if (!insurer) return null
  return insurer.proforma
}

/** Whether the pro forma button should be shown. Shown for every known insurer
 * and for the "other" freetext case — the pro forma is anchored to the
 * service, not the insurer. */
export function shouldShowProforma(
  data: InsurerData,
  insurerId: string
): boolean {
  if (insurerId === "other") return true
  return findInsurer(data, insurerId) !== undefined
}
